import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { api } from "@/lib/api";
import { drawPurchaseOrder } from "@/lib/poPdf";
import { StatusBadge, money, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, ClockCounterClockwise } from "@phosphor-icons/react";

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);

  const load = useCallback(() => {
    api.get(`/purchase-orders/${id}`).then((r) => setPo(r.data)).catch(() => toast.error("Purchase order not found"));
  }, [id]);
  useEffect(load, [load]);

  const downloadPDF = () => {
    const doc = new jsPDF();
    drawPurchaseOrder(doc, po);
    doc.save(`${po.po_number}.pdf`);
  };

  if (!po) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate("/purchase-orders")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-pos">
        <ArrowLeft size={16} /> Purchase Orders
      </button>

      <div className="hp-card p-8">
        <div className="flex items-start justify-between border-b border-border pb-6">
          <div>
            <img src="/heliopolis-logo.png" alt="Heliopolis" className="h-16 mb-3" />
            <p className="text-xs text-muted-foreground">ops@heliopolispharmaceuticals.com</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Purchase Order</p>
            <p className="font-heading text-2xl font-extrabold tracking-tight">{po.po_number}</p>
            <div className="mt-2 flex justify-end"><StatusBadge status={po.status} /></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 py-6 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Supplier</p>
            <p className="font-medium mt-1">{po.supplier_name}</p>
            {po.supplier?.email && <p className="text-muted-foreground">{po.supplier.email}</p>}
            {po.supplier?.country && <p className="text-muted-foreground">{po.supplier.country}</p>}
          </div>
          <div className="text-right">
            <p className="text-muted-foreground">Expected {fmtDate(po.expected_date)}</p>
            {po.order_id && <p className="text-muted-foreground">Linked to an order</p>}
          </div>
        </div>

        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-y border-border">
              <th className="py-2 font-semibold">Product</th>
              <th className="py-2 font-semibold text-right">Qty</th>
              <th className="py-2 font-semibold text-right">Cost price</th>
              <th className="py-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {po.items.map((it, i) => (
              <tr key={i} className="border-b border-border/60">
                <td className="py-2 font-medium">{it.name}</td>
                <td className="py-2 text-right">{it.quantity}</td>
                <td className="py-2 text-right">{money(it.unit_price)}</td>
                <td className="py-2 text-right">{money(it.quantity * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <div className="flex justify-end mt-6">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between border-t border-border pt-2 font-heading font-extrabold text-lg"><span>Total (cost)</span><span data-testid="po-detail-total">{money(po.total)}</span></div>
          </div>
        </div>

        {po.notes && <p className="text-sm text-muted-foreground mt-6 border-t border-border pt-4"><span className="font-semibold">Notes:</span> {po.notes}</p>}
      </div>

      <div className="flex gap-3">
        <Button className="rounded-sm" onClick={downloadPDF} data-testid="download-po-pdf">
          <DownloadSimple size={16} className="mr-2" /> Export PO (PDF)
        </Button>
      </div>

      {po.audit && po.audit.length > 0 && (
        <div className="hp-card p-6" data-testid="po-audit-trail">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <ClockCounterClockwise size={16} className="text-primary" /> Edit history
          </p>
          <ul className="space-y-2">
            {[...po.audit].reverse().map((a, i) => (
              <li key={i} className="text-sm flex items-center justify-between gap-3">
                <span>{a.action}</span>
                <span className="text-muted-foreground text-xs">{a.by} · {new Date(a.ts).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
